import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { initMetaPixel, trackPurchase, getMetaCookies } from "@/lib/metaPixel";
import { UploadView } from "@/components/UploadView";
import { HistoryView } from "@/components/HistoryView";
import { Backdrop } from "@/components/Backdrop";
import { TiltCard } from "@/components/TiltCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHORTCUTS = [
  { label: "+100rb", amount: 100000 },
  { label: "+500rb", amount: 500000 },
  { label: "+1jt", amount: 1000000 },
  { label: "+5jt", amount: 5000000 },
];

function formatRupiah(digits) {
  if (!digits) return "Rp 0";
  return "Rp " + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function onlyDigits(value) {
  return (value || "").replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 12);
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-indigo-200/40 outline-none transition-colors focus:border-indigo-400/60 focus:ring-2 focus:ring-indigo-500/20";
const labelCls = "mb-1.5 block text-xs font-semibold tracking-wide text-indigo-100/80";
const primaryBtn =
  "w-full rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 px-4 py-3 text-sm font-semibold tracking-wide text-white shadow-lg shadow-indigo-500/25 transition hover:from-indigo-400 hover:to-violet-500 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60";

export default function App() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [amountDigits, setAmountDigits] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("form"); // form | loading | success | error
  const [tab, setTab] = useState("form"); // form | upload | riwayat
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetch(`${API}/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cfg && cfg.meta_pixel_id) initMetaPixel(cfg.meta_pixel_id);
      })
      .catch(() => {});
  }, []);

  const amountValue = useMemo(
    () => (amountDigits ? parseInt(amountDigits, 10) : 0),
    [amountDigits]
  );

  const addAmount = (amount) => {
    const next = (amountValue + amount).toString();
    setAmountDigits(next);
    setErrors((e) => ({ ...e, amount: undefined }));
  };

  const onAmountChange = (e) => {
    setAmountDigits(onlyDigits(e.target.value));
    setErrors((prev) => ({ ...prev, amount: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = "Nama wajib diisi";
    if (!phone.trim()) {
      next.phone = "Nomor WhatsApp wajib diisi";
    } else if (!/^08\d{7,11}$/.test(phone.replace(/\D/g, ""))) {
      next.phone = "Pakai format 08xxxxxxxxxx";
    }
    if (!amountValue || amountValue <= 0) next.amount = "Nominal harus lebih dari 0";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    if (!validate()) return;

    setStatus("loading");
    try {
      const { fbp, fbc } = getMetaCookies();
      const response = await fetch(`${API}/purchases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          purchase_value: amountValue,
          notes: notes.trim(),
          fbp,
          fbc,
          event_source_url: window.location.href,
        }),
      });
      if (!response.ok) throw new Error("save failed");
      const data = await response.json();
      trackPurchase(data.event_id, amountValue);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  const resetForm = () => {
    setName("");
    setPhone("");
    setAmountDigits("");
    setNotes("");
    setErrors({});
    setStatus("form");
  };

  const tabs = [
    { key: "form", label: "Input", testid: "tab-form" },
    { key: "upload", label: "Upload", testid: "tab-upload" },
    { key: "riwayat", label: "Riwayat", testid: "tab-history" },
  ];

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <Backdrop />
      <div className="relative z-10 flex min-h-screen items-start justify-center px-4 py-8 sm:items-center sm:py-12">
        <div className={`w-full ${tab === "riwayat" ? "max-w-3xl" : "max-w-[420px]"}`}>
          <div
            data-testid="page-tabs"
            className="mb-4 flex rounded-xl border border-white/10 bg-white/5 p-1 backdrop-blur-md"
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={t.testid}
                onClick={() => setTab(t.key)}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                  tab === t.key
                    ? "bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/30"
                    : "text-indigo-200/60 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "form" &&
            (status === "success" ? (
              <TiltCard
                data-testid="success-state"
                className="glass enter-up rounded-2xl px-6 py-10 text-center"
              >
                <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/30">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold tracking-tight text-white">Tersimpan</h2>
                <p className="mt-1.5 text-sm text-indigo-200/60">Data pembelian masuk.</p>
                <button
                  data-testid="input-again-button"
                  onClick={resetForm}
                  className={`${primaryBtn} mt-6`}
                >
                  Input lagi
                </button>
              </TiltCard>
            ) : (
              <TiltCard
                data-testid="purchase-form-card"
                className="glass enter-up rounded-2xl px-6 py-8"
              >
                <div className="mb-7">
                  <span
                    data-testid="internal-sales-badge"
                    className="inline-block rounded-full border border-indigo-400/30 bg-indigo-500/10 px-2.5 py-1 text-[10px] font-semibold tracking-[0.14em] text-indigo-200"
                  >
                    INTERNAL SALES
                  </span>
                  <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-white">
                    NeuroAcademy
                  </h1>
                  <p className="mt-0.5 text-sm text-indigo-200/60">Catat pembelian masuk</p>
                </div>

                {status === "error" && (
                  <div
                    data-testid="error-message"
                    className="mb-5 rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
                  >
                    Gagal menyimpan. Coba lagi.
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  <div>
                    <label htmlFor="buyer-name" className={labelCls}>
                      Nama pembeli
                    </label>
                    <input
                      id="buyer-name"
                      data-testid="buyer-name-input"
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      placeholder="Nama lengkap"
                      autoComplete="name"
                      className={inputCls}
                    />
                    {errors.name && (
                      <p data-testid="buyer-name-error" className="mt-1.5 text-xs text-red-300">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="buyer-phone" className={labelCls}>
                      Nomor WhatsApp
                    </label>
                    <input
                      id="buyer-phone"
                      data-testid="buyer-phone-input"
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value.replace(/\D/g, "").slice(0, 14));
                        setErrors((prev) => ({ ...prev, phone: undefined }));
                      }}
                      placeholder="08xxxxxxxxxx"
                      autoComplete="tel"
                      className={inputCls}
                    />
                    <p
                      data-testid="buyer-phone-helper"
                      className="mt-1.5 text-xs text-indigo-200/40"
                    >
                      Awali dengan 08
                    </p>
                    {errors.phone && (
                      <p data-testid="buyer-phone-error" className="mt-1 text-xs text-red-300">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="purchase-amount" className={labelCls}>
                      Nominal pembelian
                    </label>
                    <input
                      id="purchase-amount"
                      data-testid="purchase-amount-input"
                      type="text"
                      inputMode="numeric"
                      value={formatRupiah(amountDigits)}
                      onChange={onAmountChange}
                      placeholder="Rp 0"
                      className={`${inputCls} font-medium`}
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      {SHORTCUTS.map((s) => (
                        <button
                          key={s.label}
                          type="button"
                          data-testid={`shortcut-${s.label.replace("+", "add-")}`}
                          onClick={() => addAmount(s.amount)}
                          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-indigo-100 transition hover:border-indigo-400/50 hover:bg-indigo-500/20 active:scale-[0.97]"
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    {errors.amount && (
                      <p
                        data-testid="purchase-amount-error"
                        className="mt-1.5 text-xs text-red-300"
                      >
                        {errors.amount}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="purchase-notes" className={labelCls}>
                      Catatan
                    </label>
                    <textarea
                      id="purchase-notes"
                      data-testid="purchase-notes-input"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Opsional"
                      rows={3}
                      className={`${inputCls} resize-none`}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="submit-button"
                    disabled={status === "loading"}
                    className={primaryBtn}
                  >
                    {status === "loading" ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-90"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                        Menyimpan…
                      </span>
                    ) : (
                      "Simpan"
                    )}
                  </button>
                </form>
              </TiltCard>
            ))}

          {tab === "upload" && <UploadView />}
          {tab === "riwayat" && <HistoryView />}
        </div>
      </div>
    </div>
  );
}
